import React from 'react';
import {
  familyIncomeOptions,
  financingTypeOptions,
  parentEducationOptions
} from '../../lib/domain-data';
import {
  FamilyIncomeBand,
  FinancingType,
  ParentEducation,
  SocioEconomicData
} from '../../types/domain';

/**
 * The four FR-03 fields as a controlled fieldset (SoW Bagian 2.3).
 *
 * Extracted rather than written into a page because FR-03 has to be editable
 * in three places — onboarding, the patient's own family-data screen, and the
 * doctor dashboard — and the day the three drift apart is the day one of them
 * stops offering "Tidak ingin menjawab".
 *
 * It renders `.field` markup and nothing else: no card, no heading, no submit
 * button. The host page owns the layout, so this drops cleanly into FR-02's
 * profile editor when that lands.
 */

/** An empty answer set — what a profile with no FR-03 data reads as. */
export const emptySocioEconomic: SocioEconomicData = {
  parentEducation: null,
  familyIncome: null,
  financingType: null,
  parentContact: null
};

/** Lifts the four columns off a profile row into the editor's shape. */
export function socioEconomicFromProfile(profile: {
  parent_education?: string | null;
  family_income?: string | null;
  financing_type?: string | null;
  parent_contact?: string | null;
} | null): SocioEconomicData {
  if (!profile) {
    return emptySocioEconomic;
  }
  return {
    parentEducation: (profile.parent_education as ParentEducation) ?? null,
    familyIncome: (profile.family_income as FamilyIncomeBand) ?? null,
    financingType: (profile.financing_type as FinancingType) ?? null,
    parentContact: profile.parent_contact ?? null
  };
}

/**
 * Whether every FR-03 field has an answer — drives the "lengkapi" nudges.
 *
 * `ignoreIncome` is for a surface that hides the income field: there the
 * patient has no way to answer it, so it cannot count against them.
 */
export function isSocioEconomicComplete(
  data: SocioEconomicData,
  { ignoreIncome = false }: { ignoreIncome?: boolean } = {}
): boolean {
  return (
    data.parentEducation !== null &&
    (ignoreIncome || data.familyIncome !== null) &&
    data.financingType !== null &&
    !!data.parentContact
  );
}

interface SocioEconomicFieldsProps {
  value: SocioEconomicData;
  onChange: (value: SocioEconomicData) => void;
  disabled?: boolean;
  /** Prefix for the input ids, so two instances on one page stay unique. */
  idPrefix?: string;
  /**
   * Leaves out the income picker. The stored answer is kept in `value`
   * untouched, so saving the other fields does not clear it.
   */
  hideIncome?: boolean;
}

export const SocioEconomicFields: React.FC<SocioEconomicFieldsProps> = ({
  value,
  onChange,
  disabled = false,
  idPrefix = 'socio',
  hideIncome = false
}) => {
  const set = <K extends keyof SocioEconomicData>(
    key: K,
    next: SocioEconomicData[K]
  ) => onChange({ ...value, [key]: next });

  return (
    <div className="field-group">
      <div className="field">
        <label htmlFor={`${idPrefix}-education`}>Pendidikan orang tua</label>
        <select
          id={`${idPrefix}-education`}
          value={value.parentEducation ?? ''}
          disabled={disabled}
          onChange={event =>
            set(
              'parentEducation',
              (event.target.value || null) as ParentEducation | null
            )
          }
        >
          <option value="">Pilih pendidikan terakhir</option>
          {parentEducationOptions.map(option => (
            <option key={option.id} value={option.id}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {hideIncome ? null : (
        <div className="field">
          <label htmlFor={`${idPrefix}-income`}>Penghasilan keluarga per bulan</label>
          <select
            id={`${idPrefix}-income`}
            value={value.familyIncome ?? ''}
            disabled={disabled}
            onChange={event =>
              set(
                'familyIncome',
                (event.target.value || null) as FamilyIncomeBand | null
              )
            }
          >
            <option value="">Pilih rentang penghasilan</option>
            {familyIncomeOptions.map(option => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="field">
        <label htmlFor={`${idPrefix}-financing`}>Jenis pembiayaan</label>
        <select
          id={`${idPrefix}-financing`}
          value={value.financingType ?? ''}
          disabled={disabled}
          onChange={event =>
            set('financingType', (event.target.value || null) as FinancingType | null)
          }
        >
          <option value="">Pilih jenis pembiayaan</option>
          {financingTypeOptions.map(option => (
            <option key={option.id} value={option.id}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <div className="field">
        <label htmlFor={`${idPrefix}-contact`}>Nomor kontak orang tua</label>
        {/* type="tel" so a phone shows the number pad. Not type="number": it
            would strip a leading 0 and offer a spinner on a phone number. */}
        <input
          id={`${idPrefix}-contact`}
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          placeholder="081234567890"
          value={value.parentContact ?? ''}
          disabled={disabled}
          onChange={event => set('parentContact', event.target.value || null)}
        />
      </div>
    </div>
  );
};

export default SocioEconomicFields;
